import { useLoaderData, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Utils
import cn from '@/utils/cn';
import formatDate from '@/utils/formatDate';
import { useNormalizedLink } from '@/hooks/useNormalizedLink';

// Components
import PreTitle from '@/components/atoms/PreTitle/PreTitle';
import Heading from '@/components/atoms/Heading/Heading';
import Button from '@/components/atoms/Button/Button';
import CmsImage from '@/components/atoms/CmsImage/CmsImage';

// Other
import { type SectionActualiteType } from '@/services/blocksSchema';
import ArrowRightRed from '@/assets/icons/arrow-right-red.svg?react';
import type { homePageLoader } from '@/views/Home/Home';

// Styles
import styles from './SectionActualite.module.css';

interface SectionActualiteProps {
  button: SectionActualiteType['button'];
}

function SectionActualite({
  button,
}: SectionActualiteProps) {
  const { t } = useTranslation();
  const normalizeLink = useNormalizedLink();

  // Services
  const { latestNews } = useLoaderData() as Awaited<ReturnType<typeof homePageLoader>>;

  if (!latestNews) return null;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles['content-header']}>
          <PreTitle
            preTitle={t('section-actualite.pre-title')}
          />
          <Heading
            title={t('section-actualite.title')}
            size='h2'
            Tag='h2'
            color='dark'
            align='center'
          />
        </div>
        <div className={styles.cards}>
          {
            latestNews.map((item) => (
              <div key={item.documentId} className={styles.card}>
                {
                  item.image ? (
                    <CmsImage
                      media={item.image}
                      sizes="(max-width: 1024px) 100vw, 33vw"
                      height={200}
                      className={cn([
                        styles['card-image'],
                        styles[`card-image-${item.imagePosition}`]
                      ])}
                    />
                  ) : null
                }
                <div className={styles['card-content']}>
                  {
                    item.title ? (
                      <Heading 
                        title={item.title}
                        size='h4'
                        Tag='h4'
                        color='dark'
                      />
                    ) : null
                  }
                  {
                    item.description ? (
                      <p className={styles['card-teaser']}>
                        {item.description}
                      </p>
                    ) : null
                  }
                  <div className={styles['dotted-line']} />
                  <div className={styles['card-footer']}>
                    <PreTitle
                      preTitle={formatDate(item.publicationDate)}
                    />
                    {
                      item.url ? (
                        <Link to={normalizeLink(item.url)} className={styles['card-link']}>
                          {t('section-actualite.read-article')}
                          <ArrowRightRed />
                        </Link>
                      ) : null
                    }
                  
                  </div>
                </div>
              </div>
            ))
          }
        </div>
        <Button
          title={button.title}
          url={button.url}
          bgColor='red'
        />
      </div>
    </div>
  );
}

export default SectionActualite;
